import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import ProtectedImage from '../components/ProtectedImage.jsx';
import { api, openProtectedAsset } from '../lib/api.js';

const PAGE_SIZE = 12;
const TABS = [
  { id: 'all', label: 'Todas' },
  { id: 'recent', label: 'Recentes' },
  { id: 'most-used', label: 'Mais usadas' },
];

function formatBytes(bytes) {
  if (!bytes) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function prettyDate(value) {
  if (!value) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));
}

function parseTags(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function TabButton({ active, children, onClick, badge }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11px] font-semibold transition-colors ${active
        ? 'border-sky-500 bg-sky-500 text-white'
        : 'border-[color:var(--outline-variant)] bg-surface text-on-surface'
        }`}
    >
      {children}
      {badge !== undefined ? <span className={`rounded-full px-2 py-0.5 text-[10px] ${active ? 'bg-white/20' : 'bg-slate-100 text-slate-700'}`}>{badge}</span> : null}
    </button>
  );
}

export default function ImageLibrary({ user }) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const templateId = query.get('templateId') || '';
  const targetId = query.get('target') || '';
  const canManage = user?.role === 'ADMIN' || user?.role === 'DESIGNER';

  const [items, setItems] = useState([]);
  const [selectedAssetsMap, setSelectedAssetsMap] = useState({});
  const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 1 });
  const [summary, setSummary] = useState({ allCount: 0, recentCount: 0, mostUsedCount: 0 });
  const [activeTab, setActiveTab] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [savingId, setSavingId] = useState('');
  const [editingId, setEditingId] = useState('');
  const [editCategory, setEditCategory] = useState('');
  const [editTags, setEditTags] = useState('');
  const [uploadCategory, setUploadCategory] = useState('Geral');
  const [uploadTags, setUploadTags] = useState('');
  const [batchCategory, setBatchCategory] = useState('Geral');
  const [batchTags, setBatchTags] = useState('');
  const [batchSaving, setBatchSaving] = useState(false);
  const [batchInserting, setBatchInserting] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  async function load(page = 1, overrides = {}) {
    try {
      setLoading(true);
      setError('');
      const params = {
        page,
        pageSize: PAGE_SIZE,
        tab: overrides.tab ?? activeTab,
        category: overrides.category ?? filterCategory,
        search: overrides.search ?? search,
      };
      const data = await api.listImageAssets(params);
      const nextItems = data.items || [];
      setItems(nextItems);
      setPagination(data.pagination || { page: 1, pageSize: PAGE_SIZE, totalItems: 0, totalPages: 1 });
      setSummary(data.summary || { allCount: 0, recentCount: 0, mostUsedCount: 0 });
      setSelectedAssetsMap((current) => {
        const next = { ...current };
        nextItems.forEach((item) => {
          if (next[item.id]) next[item.id] = item;
        });
        return next;
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filterCategory, search]);

  async function handleUploadFiles(filesLike) {
    const files = Array.from(filesLike || []);
    if (!files.length) return;
    try {
      setUploading(true);
      setError('');
      await api.uploadImageAssets(files, {
        category: uploadCategory,
        tags: parseTags(uploadTags),
      });
      setMessage(`${files.length} imagem(ns) enviada(s) para a biblioteca.`);
      await load(1);
    } catch (e) {
      setError(e.message);
    } finally {
      setUploading(false);
      setDragActive(false);
    }
  }

  async function handleUpload(event) {
    await handleUploadFiles(event.target.files);
    event.target.value = '';
  }

  async function handleDelete(item) {
    if (!confirm(`Excluir a imagem "${item.originalName}" da biblioteca?`)) return;
    try {
      setDeletingId(item.id);
      setError('');
      await api.deleteImageAsset(item.id);
      setSelectedAssetsMap((current) => {
        const next = { ...current };
        delete next[item.id];
        return next;
      });
      setMessage(`Imagem "${item.originalName}" removida.`);
      await load(Math.min(pagination.page, Math.max(1, pagination.totalPages)));
    } catch (e) {
      setError(e.message);
    } finally {
      setDeletingId('');
    }
  }

  function startEditing(item) {
    setEditingId(item.id);
    setEditCategory(item.category || 'Geral');
    setEditTags((item.tags || []).join(', '));
  }

  async function handleSaveMetadata(item) {
    try {
      setSavingId(item.id);
      setError('');
      await api.updateImageAsset(item.id, {
        category: editCategory,
        tags: parseTags(editTags),
      });
      setMessage(`Metadados de "${item.originalName}" atualizados.`);
      setEditingId('');
      setEditCategory('');
      setEditTags('');
      await load(pagination.page);
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingId('');
    }
  }

  async function handleUse(item) {
    if (!templateId) return;
    try {
      await api.markImageAssetUsed(item.id);
    } catch {
      /* seguir mesmo se a marcação falhar */
    }

    const params = new URLSearchParams();
    params.set('assetId', item.id);
    params.set('assetUrl', item.url);
    params.set('assetName', item.originalName || item.fileName);
    params.set('assetCategory', item.category || 'Geral');
    params.set('assetTags', JSON.stringify(item.tags || []));
    if (targetId) {
      params.set('target', targetId);
      params.set('replace', '1');
    }
    navigate(`/templates/${templateId}/editar?${params.toString()}`);
  }

  function toggleSelection(item) {
    setSelectedAssetsMap((current) => {
      const next = { ...current };
      if (next[item.id]) {
        delete next[item.id];
      } else {
        if (targetId) return { [item.id]: item };
        next[item.id] = item;
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedAssetsMap({});
  }

  function toggleCurrentPageSelection() {
    const allSelected = items.length > 0 && items.every((item) => selectedAssetsMap[item.id]);
    setSelectedAssetsMap((current) => {
      const next = { ...current };
      if (allSelected) {
        items.forEach((item) => {
          delete next[item.id];
        });
      } else {
        items.forEach((item) => {
          next[item.id] = item;
        });
      }
      return next;
    });
  }

  async function handleApplyBatchMetadata() {
    const selectedAssets = Object.values(selectedAssetsMap);
    if (!selectedAssets.length) return;

    try {
      setBatchSaving(true);
      setError('');
      await Promise.all(
        selectedAssets.map((item) =>
          api.updateImageAsset(item.id, {
            category: batchCategory,
            tags: parseTags(batchTags),
          }),
        ),
      );
      setMessage(`Metadados aplicados a ${selectedAssets.length} imagem(ns).`);
      await load(pagination.page);
    } catch (e) {
      setError(e.message);
    } finally {
      setBatchSaving(false);
    }
  }

  async function handleInsertSelected() {
    const selectedAssets = Object.values(selectedAssetsMap);
    if (!templateId || !selectedAssets.length) return;

    try {
      setBatchInserting(true);
      await Promise.allSettled(selectedAssets.map((item) => api.markImageAssetUsed(item.id)));

      if (targetId) {
        const item = selectedAssets[0];
        const params = new URLSearchParams();
        params.set('assetId', item.id);
        params.set('assetUrl', item.url);
        params.set('assetName', item.originalName || item.fileName);
        params.set('assetCategory', item.category || 'Geral');
        params.set('assetTags', JSON.stringify(item.tags || []));
        params.set('target', targetId);
        params.set('replace', '1');
        navigate(`/templates/${templateId}/editar?${params.toString()}`);
        return;
      }

      const bundleKey = `image-library-selection:${Date.now()}:${selectedAssets.length}`;
      sessionStorage.setItem(bundleKey, JSON.stringify(selectedAssets.map((item) => ({
        assetId: item.id,
        assetUrl: item.url,
        assetName: item.originalName || item.fileName,
        assetCategory: item.category || 'Geral',
        assetTags: item.tags || [],
      }))));
      navigate(`/templates/${templateId}/editar?assetBundleKey=${encodeURIComponent(bundleKey)}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBatchInserting(false);
    }
  }

  const selectedAssets = useMemo(() => Object.values(selectedAssetsMap), [selectedAssetsMap]);
  const selectedCount = selectedAssets.length;
  const allCurrentSelected = items.length > 0 && items.every((item) => selectedAssetsMap[item.id]);

  const categoryOptions = useMemo(() => {
    const values = new Set(['Geral']);
    [...items, ...selectedAssets].forEach((item) => values.add(item.category || 'Geral'));
    return Array.from(values).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [items, selectedAssets]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={templateId ? `/templates/${templateId}/editar` : '/templates'} className="text-[10px] font-semibold text-primary">
            ← Voltar {templateId ? 'para o editor' : 'ao estúdio'}
          </Link>
          <h1 className="studio-uniform-title mt-2 font-h2 text-h2 tracking-[-0.04em] text-on-surface">Biblioteca de imagens</h1>
        </div>
      </div>

      {targetId ? (
        <div className="rounded-[12px] border border-sky-200 bg-sky-50 px-4 py-3 text-[11px] text-sky-900">
          Modo substituição ativo: a imagem escolhida volta para o editor e substitui o objeto atual mantendo posição e tamanho.
        </div>
      ) : null}
      {message ? <div className="rounded-[10px] border border-emerald-200 bg-emerald-50 px-4 py-3 text-[11px] text-emerald-800">{message}</div> : null}
      {error ? <div className="rounded-[10px] border border-rose-200 bg-rose-50 px-4 py-3 text-[11px] text-rose-800">{error}</div> : null}

      <div className="rounded-[18px] border border-[color:var(--outline-variant)] bg-surface p-4 shadow-[0_18px_40px_rgba(15,23,42,0.08)]">
        <div
          onDragEnter={(event) => {
            event.preventDefault();
            if (canManage) setDragActive(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (canManage) setDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            if (event.currentTarget.contains(event.relatedTarget)) return;
            setDragActive(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            if (!canManage) return;
            setDragActive(false);
            void handleUploadFiles(event.dataTransfer.files);
          }}
          className={`rounded-[20px] border px-4 py-4 transition-colors ${dragActive ? 'border-sky-400 bg-sky-50' : 'border-dashed border-[color:var(--outline-variant)] bg-background'}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-[12px] font-semibold text-on-surface">Upload</div>
              <div className="mt-1 text-[11px] text-on-surface-variant">Arraste arquivos para cá ou selecione imagens do computador.</div>
            </div>
            {canManage ? (
              <label className="inline-flex cursor-pointer items-center gap-2 rounded-[10px] border border-[color:var(--outline-variant)] bg-surface px-4 py-2 text-[11px] font-semibold text-on-surface shadow-[0_12px_24px_rgba(15,23,42,0.06)]">
                <span className="material-symbols-outlined text-[18px]">upload</span>
                {uploading ? 'Enviando...' : 'Selecionar imagens'}
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
              </label>
            ) : null}
          </div>

          <div className="mt-4 grid gap-3 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold text-on-surface-variant">Categoria do upload</span>
              <input
                value={uploadCategory}
                onChange={(event) => setUploadCategory(event.target.value)}
                placeholder="Ex: Logo, Produto, Selo"
                className="w-full rounded-[10px] border border-[color:var(--outline-variant)] bg-surface px-3 py-2 text-[12px] text-on-surface outline-none focus:border-primary"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-[10px] font-semibold text-on-surface-variant">Tags do upload</span>
              <input
                value={uploadTags}
                onChange={(event) => setUploadTags(event.target.value)}
                placeholder="marca, promocional, redondo"
                className="w-full rounded-[10px] border border-[color:var(--outline-variant)] bg-surface px-3 py-2 text-[12px] text-on-surface outline-none focus:border-primary"
              />
            </label>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {TABS.map((tab) => (
              <TabButton
                key={tab.id}
                active={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id)}
                badge={tab.id === 'all' ? summary.allCount : tab.id === 'recent' ? summary.recentCount : summary.mostUsedCount}
              >
                {tab.label}
              </TabButton>
            ))}
          </div>

          <div className="rounded-[12px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[11px] text-on-surface-variant">
            Página {pagination.page} de {pagination.totalPages}
          </div>
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-[260px] flex-1 flex-wrap gap-3">
            <div className="relative min-w-[220px] flex-1">
              <span className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[18px] text-on-surface-variant">search</span>
              <input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') setSearch(searchInput.trim());
                }}
                placeholder="Pesquisar por nome, categoria ou tag..."
                className="w-full rounded-[12px] border border-[color:var(--outline-variant)] bg-background py-2 pl-10 pr-3 text-[12px] text-on-surface outline-none focus:border-primary"
              />
            </div>
            <button
              type="button"
              onClick={() => setSearch(searchInput.trim())}
              className="rounded-[12px] border border-[color:var(--outline-variant)] bg-surface px-4 py-2 text-[11px] font-semibold text-on-surface"
            >
              Buscar
            </button>
            <select
              value={filterCategory}
              onChange={(event) => setFilterCategory(event.target.value)}
              className="min-w-[180px] rounded-[12px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[12px] text-on-surface outline-none focus:border-primary"
            >
              <option value="">Todas as categorias</option>
              {categoryOptions.map((category) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>
          <div className="rounded-[12px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[11px] text-on-surface-variant">
            {pagination.totalItems} resultado(s)
          </div>
        </div>

        {selectedCount > 0 ? (
          <div className="mt-4 rounded-[16px] border border-sky-200 bg-sky-50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-[12px] font-semibold text-sky-900">{selectedCount} imagem(ns) selecionada(s)</div>
                <div className="mt-1 text-[11px] text-sky-800">
                  A seleção continua mesmo quando você troca de página na biblioteca.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={toggleCurrentPageSelection}
                  className="rounded-[10px] border border-sky-200 bg-white px-3 py-2 text-[11px] font-semibold text-sky-800"
                >
                  {allCurrentSelected ? 'Desmarcar página' : 'Selecionar página'}
                </button>
                <button
                  type="button"
                  onClick={clearSelection}
                  className="rounded-[10px] border border-sky-200 bg-white px-3 py-2 text-[11px] font-semibold text-sky-800"
                >
                  Limpar seleção
                </button>
              </div>
            </div>

            <div className="mt-3 grid gap-3 lg:grid-cols-[1.2fr_1.2fr_auto_auto]">
              <label className="block">
                <span className="mb-1 block text-[10px] font-semibold text-sky-900">Categoria em lote</span>
                <input
                  value={batchCategory}
                  onChange={(event) => setBatchCategory(event.target.value)}
                  className="w-full rounded-[10px] border border-sky-200 bg-white px-3 py-2 text-[12px] text-slate-900 outline-none focus:border-sky-400"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-[10px] font-semibold text-sky-900">Tags em lote</span>
                <input
                  value={batchTags}
                  onChange={(event) => setBatchTags(event.target.value)}
                  placeholder="marca, institucional"
                  className="w-full rounded-[10px] border border-sky-200 bg-white px-3 py-2 text-[12px] text-slate-900 outline-none focus:border-sky-400"
                />
              </label>
              <button
                type="button"
                onClick={() => void handleApplyBatchMetadata()}
                disabled={batchSaving}
                className="self-end rounded-[10px] bg-sky-600 px-4 py-2 text-[11px] font-semibold text-white disabled:opacity-60"
              >
                {batchSaving ? 'Aplicando...' : 'Atualizar selecionadas'}
              </button>
              {templateId ? (
                <button
                  type="button"
                  onClick={() => void handleInsertSelected()}
                  disabled={batchInserting}
                  className="self-end rounded-[10px] bg-primary px-4 py-2 text-[11px] font-semibold text-on-primary disabled:opacity-60"
                >
                  {batchInserting ? 'Inserindo...' : 'Inserir selecionadas'}
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={toggleCurrentPageSelection}
              disabled={!items.length}
              className="rounded-[10px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[11px] font-semibold text-on-surface disabled:opacity-45"
            >
              Selecionar página
            </button>
          </div>
        )}

        {loading ? (
          <div className="mt-4 rounded-[16px] border border-dashed border-[color:var(--outline-variant)] bg-background px-6 py-10 text-center text-[12px] text-on-surface-variant">
            Carregando biblioteca...
          </div>
        ) : items.length === 0 ? (
          <div className="mt-4 rounded-[16px] border border-dashed border-[color:var(--outline-variant)] bg-background px-6 py-10 text-center text-[12px] text-on-surface-variant">
            Nenhuma imagem encontrada para este filtro.
          </div>
        ) : (
          <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {items.map((item) => {
              const isEditing = editingId === item.id;
              const isSelected = !!selectedAssetsMap[item.id];
              return (
                <article
                  key={item.id}
                  className={`overflow-hidden rounded-[18px] border bg-background shadow-[0_12px_24px_rgba(15,23,42,0.05)] ${isSelected ? 'border-sky-400 ring-2 ring-sky-200' : 'border-[color:var(--outline-variant)]'
                    }`}
                >
                  <div className="relative flex h-44 items-center justify-center border-b border-[color:var(--outline-variant)] bg-[radial-gradient(circle_at_top,#f8fafc,transparent_65%),linear-gradient(180deg,#ffffff_0%,#f4f7fb_100%)] p-4">
                    <label className="absolute right-3 top-3 flex items-center gap-2 rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-slate-800 shadow">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(item)}
                        className="h-3.5 w-3.5 rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      Selecionar
                    </label>
                    <div className="absolute left-3 top-3 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold text-white">
                      {item.useCount} uso(s)
                    </div>
                    <ProtectedImage
                      src={item.thumbnailUrl || item.url}
                      alt={item.originalName}
                      className="max-h-full max-w-full rounded-[10px] object-contain"
                      fallback={
                        <div className="flex h-full w-full items-center justify-center rounded-[10px] border border-dashed border-[color:var(--outline-variant)] text-[11px] text-on-surface-variant">
                          Falha ao carregar
                        </div>
                      }
                    />
                  </div>
                  <div className="space-y-3 p-4">
                    <div>
                      <div className="truncate text-[12px] font-semibold text-on-surface">{item.originalName}</div>
                      <div className="mt-1 text-[10px] text-on-surface-variant">Criada em {prettyDate(item.createdAt)}</div>
                      <div className="mt-1 text-[10px] text-on-surface-variant">Último uso {prettyDate(item.lastUsedAt || item.updatedAt)}</div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      <span className="rounded-full bg-sky-100 px-2.5 py-1 text-[10px] font-semibold text-sky-800">{item.category || 'Geral'}</span>
                      {(item.tags || []).map((tag) => (
                        <span key={`${item.id}-${tag}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] text-slate-700">
                          #{tag}
                        </span>
                      ))}
                    </div>

                    <div className="grid grid-cols-2 gap-2 rounded-[12px] border border-[color:var(--outline-variant)] bg-surface px-3 py-2 text-[10px] text-on-surface-variant">
                      <span>Tamanho</span>
                      <span className="text-right text-on-surface">{formatBytes(item.size)}</span>
                      <span>Tipo</span>
                      <span className="truncate text-right text-on-surface">{item.mimeType}</span>
                    </div>

                    {isEditing ? (
                      <div className="space-y-2 rounded-[12px] border border-[color:var(--outline-variant)] bg-surface p-3">
                        <label className="block">
                          <span className="mb-1 block text-[10px] font-semibold text-on-surface-variant">Categoria</span>
                          <input
                            value={editCategory}
                            onChange={(event) => setEditCategory(event.target.value)}
                            className="w-full rounded-[10px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[12px] text-on-surface outline-none focus:border-primary"
                          />
                        </label>
                        <label className="block">
                          <span className="mb-1 block text-[10px] font-semibold text-on-surface-variant">Tags</span>
                          <input
                            value={editTags}
                            onChange={(event) => setEditTags(event.target.value)}
                            placeholder="separe por vírgula"
                            className="w-full rounded-[10px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[12px] text-on-surface outline-none focus:border-primary"
                          />
                        </label>
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleSaveMetadata(item)}
                            disabled={savingId === item.id}
                            className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-3 py-2 text-[11px] font-semibold text-on-primary disabled:opacity-60"
                          >
                            <span className="material-symbols-outlined text-[16px]">save</span>
                            Salvar
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId('');
                              setEditCategory('');
                              setEditTags('');
                            }}
                            className="inline-flex items-center gap-2 rounded-[10px] border border-[color:var(--outline-variant)] px-3 py-2 text-[11px] font-semibold text-on-surface"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : null}

                    <div className="flex flex-wrap gap-2">
                      {templateId ? (
                        <button
                          type="button"
                          onClick={() => handleUse(item)}
                          className="inline-flex items-center gap-2 rounded-[10px] bg-primary px-3 py-2 text-[11px] font-semibold text-on-primary"
                        >
                          <span className="material-symbols-outlined text-[16px]">{targetId ? 'frame_reload' : 'add_photo_alternate'}</span>
                          {targetId ? 'Substituir mantendo tamanho' : 'Inserir na etiqueta'}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => openProtectedAsset(item.url).catch((e) => setError(e.message))}
                        className="inline-flex items-center gap-2 rounded-[10px] border border-[color:var(--outline-variant)] px-3 py-2 text-[11px] font-semibold text-on-surface"
                      >
                        <span className="material-symbols-outlined text-[16px]">open_in_new</span>
                        Visualizar
                      </button>
                      {canManage ? (
                        <>
                          <button
                            type="button"
                            onClick={() => startEditing(item)}
                            className="inline-flex items-center gap-2 rounded-[10px] border border-[color:var(--outline-variant)] px-3 py-2 text-[11px] font-semibold text-on-surface"
                          >
                            <span className="material-symbols-outlined text-[16px]">sell</span>
                            Tags e categoria
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(item)}
                            disabled={deletingId === item.id}
                            className="inline-flex items-center gap-2 rounded-[10px] border border-rose-200 bg-rose-50 px-3 py-2 text-[11px] font-semibold text-rose-700 disabled:opacity-60"
                          >
                            <span className="material-symbols-outlined text-[16px]">delete</span>
                            Excluir
                          </button>
                        </>
                      ) : null}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-[color:var(--outline-variant)] pt-4">
          <div className="text-[11px] text-on-surface-variant">
            Mostrando {items.length} item(ns) nesta página.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => load(Math.max(1, pagination.page - 1))}
              disabled={pagination.page <= 1 || loading}
              className="rounded-[10px] border border-[color:var(--outline-variant)] px-3 py-2 text-[11px] font-semibold text-on-surface disabled:opacity-45"
            >
              Anterior
            </button>
            <div className="rounded-[10px] border border-[color:var(--outline-variant)] bg-background px-3 py-2 text-[11px] text-on-surface">
              {pagination.page} / {pagination.totalPages}
            </div>
            <button
              type="button"
              onClick={() => load(Math.min(pagination.totalPages, pagination.page + 1))}
              disabled={pagination.page >= pagination.totalPages || loading}
              className="rounded-[10px] border border-[color:var(--outline-variant)] px-3 py-2 text-[11px] font-semibold text-on-surface disabled:opacity-45"
            >
              Próxima
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
